import { forwardRef, useCallback, useEffect, useRef, type TextareaHTMLAttributes } from 'react';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  showCount?: boolean;
  autoResize?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      showCount = false,
      autoResize = false,
      maxLength,
      className = '',
      id,
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    const charCount = typeof value === 'string' ? value.length : 0;

    const resize = useCallback(() => {
      const el = internalRef.current;
      if (!el || !autoResize) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }, [autoResize]);

    useEffect(() => {
      resize();
    }, [value, resize]);

    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-neutral-700">
            {label}
          </label>
        )}
        <textarea
          ref={(el) => {
            internalRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) ref.current = el;
          }}
          id={inputId}
          maxLength={maxLength}
          value={value}
          onChange={(e) => {
            onChange?.(e);
            resize();
          }}
          className={`rounded-lg border px-3 py-2 text-base text-neutral-900 placeholder-neutral-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-neutral-100 disabled:text-neutral-400 ${
            autoResize ? 'resize-none overflow-hidden' : 'resize-y'
          } ${
            error ? 'border-error focus:border-error focus:ring-error/20' : 'border-neutral-300'
          } ${className}`}
          aria-invalid={error ? 'true' : undefined}
          {...props}
        />
        <div className="flex items-center justify-between">
          {error && (
            <p className="text-sm text-error" role="alert">
              {error}
            </p>
          )}
          {showCount && maxLength && (
            <span
              className={`ml-auto text-xs ${
                charCount > maxLength * 0.9 ? 'text-error' : 'text-neutral-400'
              }`}
            >
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
